/* eslint-disable */
import { useState, useEffect } from "react";
import {
  Loader2,
  X,
  Mail,
  Shield,
  UserPlus,
  Sparkles,
  User,
  Package,
  Plus,
  Trash2,
  Camera,
  Upload,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Swal from "sweetalert2";

const InviteWorkerModal = ({
  isOpen,
  onClose,
  onInvite,
  roles = [],
  sender,
  products = [],
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "",
    sender,
    productIds: [], // ✅ multiple products
    photoUrl: "", // ✅ photo URL
  });

  const [loading, setLoading] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [roleFocused, setRoleFocused] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");

  const isConfirmer = newUser.role === "confirmer";
  const isAllProductsSelected =
    products.length > 0 &&
    newUser.productIds.length === products.length &&
    products.every((product) => newUser.productIds.includes(product.id));

  useEffect(() => {
    if (!isOpen) {
      setNewUser({
        name: "",
        email: "",
        role: "",
        sender,
        productIds: [],
        photoUrl: "",
      });
      setLoading(false);
      setPhotoPreview("");
    }
  }, [isOpen, sender]);

  useEffect(() => {
    if (!isConfirmer && newUser.productIds.length) {
      setNewUser((prev) => ({ ...prev, productIds: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirmer]);

  const handleInviteUser = async () => {
    if (!newUser.email || !newUser.role) return;
    if (isConfirmer && (!newUser.productIds || newUser.productIds.length === 0)) return;

    setLoading(true);
    await onInvite(newUser, () => setLoading(false));
    setLoading(false);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) onClose();
  };

  const addProductRow = () => {
    setNewUser((prev) => ({ ...prev, productIds: [...prev.productIds, ""] }));
  };

  const toggleSelectAllProducts = (checked) => {
    setNewUser((prev) => ({
      ...prev,
      productIds: checked ? products.map((product) => product.id) : [],
    }));
  };

  const updateProductAt = (index, value) => {
    setNewUser((prev) => {
      const next = [...prev.productIds];
      next[index] = value;
      return { ...prev, productIds: next };
    });
  };

  const removeProductAt = (index) => {
    setNewUser((prev) => {
      const next = prev.productIds.filter((_, i) => i !== index);
      return { ...prev, productIds: next };
    });
  };

  const isOptionDisabled = (productId, rowIndex) => {
    return newUser.productIds.some((id, i) => i !== rowIndex && id === productId);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validation du type de fichier
    if (!file.type.startsWith("image/")) {
      Swal.fire({
        icon: "warning",
        title: t("workersManagement.inviteWorkerModal.invalidFileTypeTitle") || t("ui.invalidFile"),
        text:
          t("workersManagement.inviteWorkerModal.invalidFileType") ||
          t("ui.selectImageFile"),
        confirmButtonText:
          t("workersManagement.inviteWorkerModal.ok") || t("ship.ok"),
      });
      return;
    }

    // Validation de la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: "warning",
        title: t("workersManagement.inviteWorkerModal.fileTooLargeTitle") || t("workersManagement.inviteWorkerModal.fileTooLargeTitle"),
        text:
          t("workersManagement.inviteWorkerModal.fileTooLarge") ||
          t("workersManagement.inviteWorkerModal.fileTooLarge"),
        confirmButtonText:
          t("workersManagement.inviteWorkerModal.ok") || t("ship.ok"),
      });
      return;
    }

    setUploadingPhoto(true);

    try {
      // Prévisualisation locale
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);

      const formData = new FormData();
      formData.append("upload", file, `${Date.now()}-${file.name}`);

      const response = await axios.post(
        "https://octopus-app-i4ylg.ondigitalocean.app/upload",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data.url) {
        setNewUser((prev) => ({ ...prev, photoUrl: response.data.url }));
      }
    } catch (error) {
      console.error("Upload failed:", error);
      Swal.fire({
        icon: "error",
        title: t("workersManagement.inviteWorkerModal.uploadErrorTitle") || t("workersManagement.inviteWorkerModal.uploadErrorTitle"),
        text:
          t("workersManagement.inviteWorkerModal.uploadError") ||
          t("workersManagement.inviteWorkerModal.uploadError"),
        confirmButtonText:
          t("workersManagement.inviteWorkerModal.ok") || t("ship.ok"),
      });
      setPhotoPreview("");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = () => {
    setNewUser((prev) => ({ ...prev, photoUrl: "" }));
    setPhotoPreview("");
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
      onClick={handleBackdropClick}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="bg-white dark:!bg-[#1e1e1e] rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col transform transition-all animate-slideUp">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-purple-500 to-purple-700 p-6 rounded-t-2xl">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12 blur-2xl" />

          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <UserPlus className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  {t("workersManagement.inviteWorkerModal.title")}
                </h2>
                <p className="text-purple-100 text-sm">
                  {t("workersManagement.inviteWorkerModal.subtitle")}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              disabled={loading}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg flex items-center justify-center transition-all disabled:opacity-50"
              title={t("workersManagement.inviteWorkerModal.cancel")}
            >
              <X className="text-white" size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-purple-400 scrollbar-track-transparent">
          <div className="space-y-5">
            {/* Photo Upload */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:!text-gray-300">
                <Camera size={16} className="text-purple-600 dark:!text-purple-400" />
                {t("workersManagement.inviteWorkerModal.photoLabel")}
              </label>

              <div className="flex items-center gap-4">
                {photoPreview || newUser.photoUrl ? (
                  <div className="relative">
                    <img
                      src={photoPreview || newUser.photoUrl}
                      alt={t("ui.workerPreview")}
                      className="w-20 h-20 rounded-xl object-cover border-2 border-purple-200 dark:!border-purple-700"
                    />
                    {!uploadingPhoto && (
                      <button
                        type="button"
                        onClick={removePhoto}
                        disabled={loading}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-all disabled:opacity-50"
                      >
                        <X size={14} />
                      </button>
                    )}
                    {uploadingPhoto && (
                      <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                      </div>
                    )}
                  </div>
                ) : (
                  <label className="relative cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={loading || uploadingPhoto}
                      className="hidden"
                    />
                    <div className="w-20 h-20 border-2 border-dashed border-gray-300 dark:!border-gray-600 rounded-xl flex items-center justify-center hover:border-purple-400 dark:!hover:border-purple-500 transition-all bg-gray-50 dark:!bg-[#2a2a2a]">
                      {uploadingPhoto ? (
                        <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                      ) : (
                        <Upload size={20} className="text-gray-400 dark:!text-gray-500" />
                      )}
                    </div>
                  </label>
                )}

                <div className="flex-1">
                  <p className="text-xs text-gray-600 dark:!text-gray-400">
                    {t("workersManagement.inviteWorkerModal.photoHint")}
                  </p>
                  <p className="text-xs text-gray-500 dark:!text-gray-500 mt-1">
                    {t("workersManagement.inviteWorkerModal.photoFormat")}
                  </p>
                </div>
              </div>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:!text-gray-300">
                <User size={16} className="text-purple-600 dark:!text-purple-400" />
                {t("workersManagement.inviteWorkerModal.nameLabel")}
              </label>

              <div className="relative">
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  onFocus={() => setNameFocused(true)}
                  onBlur={() => setNameFocused(false)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 bg-gray-50 dark:!bg-[#2a2a2a] dark:!text-white ${nameFocused
                    ? "border-purple-500 bg-white dark:!bg-[#1e1e1e] shadow-lg shadow-purple-100 dark:!shadow-purple-900/20"
                    : "border-gray-200 dark:!border-gray-700 hover:border-gray-300 dark:!hover:border-gray-600"
                    } focus:outline-none`}
                  placeholder={t("workersManagement.inviteWorkerModal.namePlaceholder")}
                  disabled={loading}
                />

                {nameFocused && (
                  <div
                    className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? "left-3" : "right-3"
                      }`}
                  >
                    <Sparkles size={16} className="text-purple-400 animate-pulse" />
                  </div>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:!text-gray-300">
                <Mail size={16} className="text-purple-600 dark:!text-purple-400" />
                {t("workersManagement.inviteWorkerModal.emailLabel")}
              </label>

              <div className="relative">
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 bg-gray-50 dark:!bg-[#2a2a2a] dark:!text-white ${emailFocused
                    ? "border-purple-500 bg-white dark:!bg-[#1e1e1e] shadow-lg shadow-purple-100 dark:!shadow-purple-900/20"
                    : "border-gray-200 dark:!border-gray-700 hover:border-gray-300 dark:!hover:border-gray-600"
                    } focus:outline-none`}
                  placeholder={t("workersManagement.inviteWorkerModal.emailPlaceholder")}
                  disabled={loading}
                />

                {emailFocused && (
                  <div
                    className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? "left-3" : "right-3"
                      }`}
                  >
                    <Sparkles size={16} className="text-purple-400 animate-pulse" />
                  </div>
                )}
              </div>
            </div>

            {/* Role */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:!text-gray-300">
                <Shield size={16} className="text-purple-600 dark:!text-purple-400" />
                {t("workersManagement.inviteWorkerModal.roleLabel")}
              </label>

              <div className="relative">
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  onFocus={() => setRoleFocused(true)}
                  onBlur={() => setRoleFocused(false)}
                  className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 bg-gray-50 dark:!bg-[#2a2a2a] dark:!text-white appearance-none cursor-pointer ${roleFocused
                    ? "border-purple-500 bg-white dark:!bg-[#1e1e1e] shadow-lg shadow-purple-100 dark:!shadow-purple-900/20"
                    : "border-gray-200 dark:!border-gray-700 hover:border-gray-300 dark:!hover:border-gray-600"
                    } focus:outline-none`}
                  disabled={loading}
                >
                  <option value="" className="dark:!bg-[#2a2a2a]">
                    {t("workersManagement.inviteWorkerModal.rolePlaceholder")}
                  </option>
                  {roles
                    .filter((r) => r.value !== "store_owner")
                    .map((role) => (
                      <option key={role.value} value={role.value} className="dark:!bg-[#2a2a2a]">
                        {role.label}
                      </option>
                    ))}
                </select>

                <div
                  className={`absolute top-1/2 -translate-y-1/2 pointer-events-none ${isRTL ? "left-3" : "right-3"
                    }`}
                >
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Products (Confirmer) */}
            {isConfirmer && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-purple-100 dark:!bg-purple-900/30 rounded-lg flex items-center justify-center">
                      <Package size={16} className="text-purple-600 dark:!text-purple-400" />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700 dark:!text-gray-300">
                        {t("workersManagement.inviteWorkerModal.assignedProducts")}
                      </label>

                      {newUser.productIds.length > 0 && (
                        <span className="mr-2 text-xs bg-purple-100 dark:!bg-purple-900/40 text-purple-700 dark:!text-purple-300 px-2 py-0.5 rounded-full">
                          {newUser.productIds.length}{" "}
                          {t("workersManagement.inviteWorkerModal.productsCount", {
                            count: newUser.productIds.length,
                          })}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:!text-gray-300">
                      <input
                        type="checkbox"
                        checked={isAllProductsSelected}
                        onChange={(e) => toggleSelectAllProducts(e.target.checked)}
                        disabled={loading || products.length === 0}
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 dark:!border-gray-700 dark:!bg-[#1e1e1e]"
                      />
                      {t("workersManagement.inviteWorkerModal.selectAllProducts")}
                    </label>

                    <button
                      type="button"
                      onClick={addProductRow}
                      disabled={loading || products.length === 0 || isAllProductsSelected}
                      className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
                    >
                      <Plus size={16} className="group-hover:rotate-90 transition-transform duration-200" />
                      {t("workersManagement.inviteWorkerModal.addProduct")}
                    </button>
                  </div>
                </div>

                {newUser.productIds.length === 0 && (
                  <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 dark:!from-[#252525] dark:!to-[#1e1e1e] border-2 border-dashed border-gray-300 dark:!border-gray-700 rounded-2xl p-8 text-center group hover:border-purple-400 dark:!hover:border-purple-600 transition-colors duration-300">
                    <div className="absolute inset-0 bg-purple-500/5 dark:!bg-purple-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    <div className="relative">
                      <div className="w-16 h-16 mx-auto bg-purple-100 dark:!bg-purple-900/30 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                        <Package size={28} className="text-purple-600 dark:!text-purple-400" />
                      </div>
                      <h4 className="text-gray-900 dark:!text-white font-medium mb-1">
                        {t("workersManagement.inviteWorkerModal.noAssignedTitle")}
                      </h4>
                      <p className="text-sm text-gray-500 dark:!text-gray-400 max-w-sm mx-auto">
                        {t("workersManagement.inviteWorkerModal.noAssignedSubtitle")}
                      </p>
                      <button
                        onClick={addProductRow}
                        className="mt-4 inline-flex items-center gap-1 text-sm text-purple-600 dark:!text-purple-400 hover:text-purple-700 dark:!hover:text-purple-300 font-medium"
                        type="button"
                      >
                        <Plus size={16} />
                        {t("workersManagement.inviteWorkerModal.addFirstProduct")}
                      </button>
                    </div>
                  </div>
                )}

                {newUser.productIds.length > 0 && (
                  <div className="space-y-3">
                    {isAllProductsSelected ? (
                      <div className="relative overflow-hidden bg-gradient-to-r from-green-50 to-green-100/50 dark:!from-green-900/20 dark:!to-green-800/10 border-2 border-green-200 dark:!border-green-800 rounded-xl p-6">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer" />

                        <div className="relative flex items-center justify-center gap-3">
                          <div className="w-12 h-12 bg-green-100 dark:!bg-green-900/30 rounded-lg flex items-center justify-center">
                            <Package size={24} className="text-green-600 dark:!text-green-400" />
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-semibold text-green-900 dark:!text-green-300 mb-1">
                              {t("workersManagement.inviteWorkerModal.allProductsSelectedTitle") || t("workersManagement.inviteWorkerModal.allProductsSelectedTitle")}
                            </p>
                            <p className="text-sm text-green-700 dark:!text-green-400">
                              {products.length}{" "}
                              {t("workersManagement.inviteWorkerModal.productsCount", {
                                count: products.length,
                              })}
                            </p>
                          </div>
                        </div>

                        <button
                          onClick={() => toggleSelectAllProducts(false)}
                          type="button"
                          className="absolute top-2 right-2 text-green-600 dark:!text-green-400 hover:text-green-700 dark:!hover:text-green-300 transition-colors"
                          title={t("workersManagement.inviteWorkerModal.deselect")}
                        >
                          <X size={20} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 gap-3">
                          {newUser.productIds.map((productId, idx) => (
                            <div
                              key={idx}
                              className="group relative bg-white dark:!bg-[#2a2a2a] rounded-xl border-2 border-gray-200 dark:!border-gray-700 hover:border-purple-400 dark:!hover:border-purple-600 transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 w-1 h-8 bg-purple-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />

                              <div className="flex items-center gap-3 p-3">
                                <div className="flex-shrink-0 w-10 h-10 bg-purple-100 dark:!bg-purple-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                                  <Package size={18} className="text-purple-600 dark:!text-purple-400" />
                                </div>

                                <div className="flex-1 min-w-0">
                                  <div className="relative">
                                    <select
                                      value={productId}
                                      onChange={(e) => updateProductAt(idx, e.target.value)}
                                      className="w-full px-4 py-3 pr-10 bg-gray-50 dark:!bg-[#1e1e1e] border-2 border-gray-200 dark:!border-gray-700 rounded-xl text-gray-900 dark:!text-white focus:border-purple-500 dark:!focus:border-purple-500 focus:ring-0 focus:outline-none hover:bg-white dark:!hover:bg-[#252525] transition-all appearance-none cursor-pointer"
                                      disabled={loading}
                                    >
                                      <option value="" className="dark:!bg-[#1e1e1e]">
                                        {t("workersManagement.inviteWorkerModal.selectProduct")}
                                      </option>
                                      {products.map((p) => (
                                        <option
                                          key={p.id}
                                          value={p.id}
                                          disabled={isOptionDisabled(p.id, idx)}
                                          className="dark:!bg-[#1e1e1e]"
                                        >
                                          {p?.name || t("workersManagement.table.noNameProduct")}
                                        </option>
                                      ))}
                                    </select>

                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </div>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => removeProductAt(idx)}
                                  disabled={loading}
                                  className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-50 dark:!bg-red-900/20 text-red-600 dark:!text-red-400 hover:bg-red-100 dark:!hover:bg-red-900/30 hover:text-red-700 dark:!hover:text-red-300 transition-all disabled:opacity-50 flex items-center justify-center group-hover:scale-110"
                                  title={t("workersManagement.inviteWorkerModal.deleteProduct")}
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>

                              {productId && (
                                <div className="absolute -top-2 -right-2">
                                  <span className="px-2 py-1 text-xs bg-green-100 dark:!bg-green-900/30 text-green-700 dark:!text-green-400 rounded-full border-2 border-white dark:!border-[#2a2a2a]">
                                    {t("workersManagement.inviteWorkerModal.selectedBadge")}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>

                        {products.length > newUser.productIds.length && (
                          <div className="flex justify-center mt-2">
                            <button
                              onClick={addProductRow}
                              type="button"
                              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-purple-600 dark:!text-purple-400 hover:text-purple-700 dark:!hover:text-purple-300 font-medium transition-colors"
                            >
                              <Plus size={16} className="border-2 border-current rounded-full" />
                              {t("workersManagement.inviteWorkerModal.addProduct")}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="relative overflow-hidden bg-gradient-to-r from-amber-50 to-amber-100/50 dark:!from-amber-900/20 dark:!to-amber-800/10 border border-amber-200 dark:!border-amber-800 rounded-xl p-4">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer" />

                  <div className="flex items-start gap-3 relative">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-amber-100 dark:!bg-amber-900/30 rounded-lg flex items-center justify-center">
                        <Shield size={16} className="text-amber-600 dark:!text-amber-400" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-900 dark:!text-amber-300 mb-0.5">
                        {t("workersManagement.inviteWorkerModal.confirmerRequiresTitle")}
                      </p>
                      <p className="text-xs text-amber-700 dark:!text-amber-400">
                        {t("workersManagement.inviteWorkerModal.confirmerRequiresDesc")}
                        {!newUser.productIds.length &&
                          t("workersManagement.inviteWorkerModal.confirmerRequiresAddNow")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-purple-50 dark:!bg-purple-900/20 border border-purple-100 dark:!border-purple-800 rounded-xl p-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 dark:!bg-purple-800 rounded-lg flex items-center justify-center">
                    <Mail className="text-purple-600 dark:!text-purple-400" size={16} />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-purple-900 dark:!text-purple-300 font-medium mb-1">
                    {t("workersManagement.inviteWorkerModal.importantNoteTitle")}
                  </p>
                  <p className="text-xs text-purple-700 dark:!text-purple-400">
                    {t("workersManagement.inviteWorkerModal.importantNoteDesc")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={handleInviteUser}
              disabled={
                loading ||
                !newUser.email ||
                !newUser.role ||
                (isConfirmer && (!newUser.productIds || newUser.productIds.length === 0)) ||
                (isConfirmer && newUser.productIds.some((id) => !id))
              }
              className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t("workersManagement.inviteWorkerModal.sending")}</span>
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5" />
                  <span>{t("workersManagement.inviteWorkerModal.sendInvite")}</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 bg-gray-100 dark:!bg-[#2a2a2a] text-gray-700 dark:!text-gray-300 rounded-xl font-semibold hover:bg-gray-200 dark:!hover:bg-[#3a3a3a] transition-all duration-200 disabled:opacity-50 transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {t("workersManagement.inviteWorkerModal.cancel")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteWorkerModal;